import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, Bug, ClipboardCopy, Power, RefreshCw, ServerCrash, Webhook } from 'lucide-react';
import AppShell from '../layouts/AppShell';
import AdminLayout from '../layouts/AdminLayout';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { hasAnyPermission, hasPermission } from '../utils/permissionUtils';
import { buildNavigationItems } from '../utils/navigationConfig';
import { formatMessageFullDateTime } from '../utils/dateUtils';

const formatDateTime = (value) => {
  return formatMessageFullDateTime(value) || '—';
};

const SeverityBadge = ({ value }) => {
  const style = value === 'ERROR'
    ? 'border-red-500/30 bg-red-500/10 text-red-300'
    : value === 'WARNING'
      ? 'border-amber-500/30 bg-amber-500/10 text-amber-300'
      : 'border-sky-500/30 bg-sky-500/10 text-sky-300';
  return <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${style}`}>{value}</span>;
};

// Shown only on a dev/mirror server started with DEV_SERVER_CONTROLS_ENABLED.
const DevServerSwitch = ({ requestConfig }) => {
  const [state, setState] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API}/operations/dev-server`, requestConfig())
      .then((response) => setState(response.data))
      .catch(() => setState(null));
  }, [requestConfig]);

  if (!state?.available) return null;

  const toggle = async (active) => {
    setSaving(true);
    setError('');
    try {
      const response = await axios.put(`${API}/operations/dev-server`, { active }, requestConfig());
      setState(response.data);
    } catch (toggleError) {
      setError(toggleError.response?.data?.detail || 'Unable to switch the dev server.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-[var(--tg-border-soft)] bg-[var(--tg-surface)] p-4">
      <div className="flex min-w-0 items-start gap-3">
        <Power className={`mt-0.5 h-5 w-5 shrink-0 ${state.active ? 'text-emerald-300' : 'text-[var(--tg-text-muted)]'}`} />
        <div className="min-w-0">
          <div className="flex items-center gap-2 font-semibold text-[var(--tg-text-primary)]">
            Dev server
            <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${state.active ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300' : 'border-[var(--tg-border-soft)] text-[var(--tg-text-muted)]'}`}>{state.active ? 'On' : 'Off'}</span>
          </div>
          <p className="mt-1 text-sm text-[var(--tg-text-secondary)]">
            {state.active
              ? 'Processing mirrored webhooks and lead assignments, and running background jobs. Switch off when you finish testing.'
              : 'Idle: background jobs are paused and mirrored webhooks and lead assignments are ignored. Switch on to test.'}
          </p>
          {state.updated_at && <p className="mt-1 text-xs text-[var(--tg-text-muted)]">Last switched {state.active ? 'on' : 'off'} {state.updated_by_name ? `by ${state.updated_by_name} ` : ''}at {formatDateTime(state.updated_at)}</p>}
          {!state.can_change && <p className="mt-1 text-xs text-[var(--tg-text-muted)]">Only a super admin can change this.</p>}
          {error && <p className="mt-1 text-xs text-red-300">{error}</p>}
        </div>
      </div>
      <Switch checked={state.active} onCheckedChange={toggle} disabled={saving || !state.can_change} aria-label="Dev server on or off" />
    </div>
  );
};

const OperationsPage = ({ user, onLogout }) => {
  const canViewOperations = useMemo(() => hasPermission(user, 'operations:view'), [user]);
  const navItems = useMemo(() => buildNavigationItems({
    canManageTemplates: hasPermission(user, 'template:manage'),
    canViewUserRoster: hasAnyPermission(user, ['user:manage', 'user:invite']),
    canManagePositions: hasPermission(user, 'position:manage'),
    canManageTeams: hasPermission(user, 'team:manage'),
    canInviteUsers: hasPermission(user, 'user:invite'),
    canViewStats: hasPermission(user, 'stats:view'),
    canManageIntegrations: hasPermission(user, 'integration:manage'),
    canModerateComments: hasPermission(user, 'comment:moderate'),
  }), [user]);
  const [overview, setOverview] = useState(null);
  const [logs, setLogs] = useState([]);
  const [webhookEvents, setWebhookEvents] = useState([]);
  const [severity, setSeverity] = useState('');
  const [category, setCategory] = useState('');
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('logs');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [nextBefore, setNextBefore] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const requestConfig = useCallback(() => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    timeout: 20_000,
  }), []);

  const load = useCallback(async ({ append = false, before = null } = {}) => {
    if (!canViewOperations) return;
    setLoading(true);
    setError('');
    try {
      const params = activeTab === 'logs'
        ? { limit: 50, ...(severity && { severity }), ...(category && { category }), ...(search.trim() && { search: search.trim() }), ...(before && { before }) }
        : { limit: 50, ...(before && { before }) };
      const [summary, records] = await Promise.all([
        axios.get(`${API}/operations/overview`, requestConfig()),
        axios.get(`${API}/operations/${activeTab === 'logs' ? 'logs' : 'webhook-events'}`, { ...requestConfig(), params }),
      ]);
      setOverview(summary.data);
      const newItems = records.data?.items || [];
      if (activeTab === 'logs') setLogs((current) => append ? [...current, ...newItems] : newItems);
      else setWebhookEvents((current) => append ? [...current, ...newItems] : newItems);
      setNextBefore(records.data?.next_before || null);
    } catch (loadError) {
      setError(loadError.response?.data?.detail || 'Unable to load operational diagnostics.');
    } finally {
      setLoading(false);
    }
  }, [activeTab, canViewOperations, category, requestConfig, search, severity]);

  useEffect(() => { load(); }, [load]);

  const rows = activeTab === 'logs' ? logs : webhookEvents;
  const cardData = [
    { label: 'Errors (24h)', value: overview?.errors ?? '—', icon: ServerCrash, tone: 'text-red-300' },
    { label: 'Warnings (24h)', value: overview?.warnings ?? '—', icon: AlertTriangle, tone: 'text-amber-300' },
    { label: 'Failed webhooks', value: overview?.failed_webhooks ?? '—', icon: Webhook, tone: 'text-fuchsia-300' },
    { label: 'Rate limited', value: overview?.rate_limited ?? '—', icon: Bug, tone: 'text-sky-300' },
  ];

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); } catch (_) { /* clipboard access is optional */ }
  };

  return (
    <AppShell user={user} navItems={navItems} onLogout={onLogout}>
      <AdminLayout
        title="Operations"
        description="Redacted application failures and durable webhook-processing diagnostics."
        actions={<Button variant="outline" onClick={() => load()} disabled={loading}><RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />Refresh</Button>}
      >
        {!canViewOperations ? (
          <div className="rounded-xl border border-[var(--tg-border-soft)] bg-[var(--tg-surface)] p-6 text-sm text-[var(--tg-text-secondary)]">You do not have permission to view operational logs.</div>
        ) : (
          <div className="space-y-4">
            <DevServerSwitch requestConfig={requestConfig} />
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              {cardData.map(({ label, value, icon: Icon, tone }) => (
                <div key={label} className="rounded-xl border border-[var(--tg-border-soft)] bg-[var(--tg-surface)] p-4">
                  <div className="flex items-center justify-between text-sm text-[var(--tg-text-muted)]"><span>{label}</span><Icon className={`h-4 w-4 ${tone}`} /></div>
                  <div className="mt-2 text-2xl font-semibold text-[var(--tg-text-primary)]">{value}</div>
                </div>
              ))}
            </div>

            <div className="rounded-xl border border-[var(--tg-border-soft)] bg-[var(--tg-surface)] p-3">
              <div className="flex flex-wrap items-center gap-2 border-b border-[var(--tg-border-soft)] pb-3">
                <Button variant={activeTab === 'logs' ? 'default' : 'ghost'} size="sm" onClick={() => setActiveTab('logs')}>Application failures</Button>
                <Button variant={activeTab === 'webhooks' ? 'default' : 'ghost'} size="sm" onClick={() => setActiveTab('webhooks')}>Failed webhooks</Button>
              </div>
              {activeTab === 'logs' && (
                <div className="mt-3 grid gap-2 md:grid-cols-[150px_180px_minmax(0,1fr)_auto]">
                  <select value={severity} onChange={(event) => setSeverity(event.target.value)} className="h-9 rounded-md border border-[var(--tg-border-soft)] bg-[var(--tg-sidebar-bg)] px-3 text-sm text-[var(--tg-text-primary)]">
                    <option value="">All severities</option><option value="ERROR">Errors</option><option value="WARNING">Warnings</option>
                  </select>
                  <select value={category} onChange={(event) => setCategory(event.target.value)} className="h-9 rounded-md border border-[var(--tg-border-soft)] bg-[var(--tg-sidebar-bg)] px-3 text-sm text-[var(--tg-text-primary)]">
                    <option value="">All categories</option><option value="api">API</option><option value="webhook">Webhook</option><option value="meta">Meta</option><option value="system">System</option>
                  </select>
                  <input value={search} onChange={(event) => setSearch(event.target.value)} onKeyDown={(event) => event.key === 'Enter' && load()} placeholder="Search route, source, or error text" className="h-9 min-w-0 rounded-md border border-[var(--tg-border-soft)] bg-[var(--tg-sidebar-bg)] px-3 text-sm text-[var(--tg-text-primary)] placeholder:text-[var(--tg-text-muted)]" />
                  <Button size="sm" onClick={() => load()} disabled={loading}>Apply</Button>
                </div>
              )}
            </div>

            {error && <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-200">{error}</div>}
            <div className="overflow-hidden rounded-xl border border-[var(--tg-border-soft)] bg-[var(--tg-surface)]">
              <div className="max-h-[56vh] overflow-auto">
                <table className="w-full min-w-[760px] text-left text-sm">
                  <thead className="sticky top-0 bg-[var(--tg-sidebar-bg)] text-xs uppercase tracking-wide text-[var(--tg-text-muted)]"><tr><th className="p-3">When</th><th className="p-3">Severity</th><th className="p-3">Source</th><th className="p-3">Message</th><th className="p-3">Status</th><th className="p-3">Details</th></tr></thead>
                  <tbody>
                    {rows.map((row) => (
                      <React.Fragment key={row.id}>
                        <tr className="border-t border-[var(--tg-border-soft)] align-top text-[var(--tg-text-secondary)]">
                          <td className="whitespace-nowrap p-3 text-xs">{formatDateTime(row.occurred_at)}</td>
                          <td className="p-3"><SeverityBadge value={row.severity || row.status || 'FAILED'} /></td>
                          <td className="p-3 text-xs">{row.source || `${row.platform || 'Webhook'} · ${row.event_kind || 'event'}`}</td>
                          <td className="max-w-[430px] p-3 break-words text-[var(--tg-text-primary)]">{row.message}</td>
                          <td className="p-3">{row.status_code || (row.attempts != null ? `${row.attempts} attempts` : '—')}</td>
                          <td className="p-3"><Button variant="ghost" size="sm" onClick={() => setExpandedId(expandedId === row.id ? null : row.id)}>{expandedId === row.id ? 'Hide' : 'View'}</Button></td>
                        </tr>
                        {expandedId === row.id && (
                          <tr className="border-t border-[var(--tg-border-soft)]"><td colSpan="6" className="bg-[var(--tg-sidebar-bg)] p-3">
                            <div className="mb-2 flex items-center gap-2 text-xs text-[var(--tg-text-muted)]"><span>Correlation ID: {row.request_id || row.event_id || 'not available'}</span>{(row.request_id || row.event_id) && <button type="button" onClick={() => copy(row.request_id || row.event_id)} className="rounded p-1 hover:bg-white/10" aria-label="Copy correlation ID"><ClipboardCopy className="h-3.5 w-3.5" /></button>}</div>
                            {row.details && <pre className="max-h-52 overflow-auto whitespace-pre-wrap rounded-md border border-[var(--tg-border-soft)] p-3 text-xs text-[var(--tg-text-secondary)]">{JSON.stringify(row.details, null, 2)}</pre>}
                          </td></tr>
                        )}
                      </React.Fragment>
                    ))}
                    {!loading && rows.length === 0 && <tr><td colSpan="6" className="p-10 text-center text-[var(--tg-text-muted)]">No matching diagnostics. Server failures, rate limits, and Meta message-send rejections will appear here after deployment.</td></tr>}
                  </tbody>
                </table>
              </div>
              {nextBefore && <div className="border-t border-[var(--tg-border-soft)] p-3 text-center"><Button variant="outline" size="sm" onClick={() => load({ append: true, before: nextBefore })} disabled={loading}>Load more</Button></div>}
            </div>
          </div>
        )}
      </AdminLayout>
    </AppShell>
  );
};

export default OperationsPage;
